/**
 * The words every screen uses for the integrity score, in one place.
 *
 * Moderators were reading three different "integrity" numbers -- this profile
 * (high is ordinary), the retired v1 outlier rank (high is suspicious) and
 * trust (earned from zero) -- each labelled its own way, and a "24" means
 * nothing until you know which one it is and what it rests on. Since
 * 2026-10-08 Team Tools shows only this one, and every place that shows it
 * says the same three things: what it measures, how much play it is based on,
 * and when and by which version it was scored.
 */

export const INTEGRITY_MEANING =
    'How ordinary this account plays against everyone else, out of 100. 100 is ordinary, low is unusual. ' +
    'A reason to look, never proof of cheating.'

/** Below this many matches the score rests on too little play to read at face
 *  value. 25 is the integrity gate's own minimum, so "few matches" here and
 *  "too few to gate" there mean the same thing. */
export const FEW_MATCHES = 25

export type IntegrityBasis = {
    sessions?: number | null
    computed_at?: string | null
    weights_version?: string | null
}

export const isFewMatches = (basis: IntegrityBasis) =>
    basis.sessions !== null && basis.sessions !== undefined && basis.sessions < FEW_MATCHES

const day = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

/** How long ago the profile was last scored: "today", "1 day ago", "12 days ago".
 *
 *  D-105 (option A): a profile stays current until the scoring version
 *  changes, however old it is. Expiring it by age would lift the gate on a
 *  cheater who stops playing for a while (measured 2026-09-29: 1,689 of 2,336
 *  profiles were over a day old). So the age is shown instead, on every screen
 *  that shows the score, and staleness is the reader's call. `computed_at` is
 *  restamped on every rescore, so this is the age of the last rescore. */
export const integrityAge = (computedAt: string | null | undefined): string | null => {
    if (!computedAt) return null
    const days = Math.floor((Date.now() - new Date(computedAt).getTime()) / 86_400_000)
    if (!(days >= 1)) return 'today'
    return days === 1 ? '1 day ago' : `${days.toLocaleString()} days ago`
}

/** "based on 6,163 matches · scored 8 Oct 2026, 12 days ago (profile-v4)" */
export const integrityBasisText = (basis: IntegrityBasis): string =>
    [
        basis.sessions !== null && basis.sessions !== undefined
            ? `based on ${basis.sessions.toLocaleString()} match${basis.sessions === 1 ? '' : 'es'}` +
              (isFewMatches(basis) ? ' (few, read with care)' : '')
            : null,
        basis.computed_at
            ? `scored ${day(basis.computed_at)}, ${integrityAge(basis.computed_at)}` +
              `${basis.weights_version ? ` (${basis.weights_version})` : ''}` +
              '; counts until the scoring version changes'
            : null,
    ]
        .filter(Boolean)
        .join(' · ')

/** When a finding was raised and when the nightly check last found it again.
 *  A finding not reproduced for days came from older detector code. */
export const findingDatesText = (f: { first_seen_at?: string | null; last_seen_at?: string | null }) =>
    [
        f.first_seen_at ? `found ${day(f.first_seen_at)}` : null,
        f.last_seen_at ? `last seen ${day(f.last_seen_at)}` : null,
    ]
        .filter(Boolean)
        .join(' · ')
